// Card data model — mirrors supabase-state-snapshot.json shapes.

export type CardType =
  | "Champion"
  | "Armor"
  | "Equipment"
  | "Signature Move"
  | "Stance"
  | "Utility"
  | "Attack/Defend"
  | "Token";

export type CardStatus = "Draft" | "Testing" | "Finalized";

export type Weight = "Light" | "Medium" | "Heavy" | "" | string;

export interface ChampionFields {
  speed?: string;
  ability?: string;
  faction?: string;
  championType?: string[];
  equipmentStat?: string;
}

export interface ArmorFields {
  weight?: string;
  ability?: string;
  blockValue?: string;
}

export interface EquipmentFields {
  ability?: string;
  attackType?: string;
  blockValue?: string;
  defendType?: string;
  powerValue?: string;
  staminaCost?: string;
  attackWeight?: string;
  defendWeight?: string;
  equipmentValue?: string;
  equipmentSubtype?: string;
  secondarySubtype?: string;
}

export interface SignatureMoveFields {
  ability?: string;
  condition?: string;
  attackType?: string;
  blockValue?: string;
  powerValue?: string;
  staminaCost?: string;
  attackWeight?: string;
}

export interface StanceFields {
  ability?: string;
  typeRequirement?: string[];
}

/** Triggers a Reaction-subtype utility (or future reaction-ability card) can
 *  respond to during an Action Chain reaction window. */
export type ReactionTrigger =
  | "opponent-plays-utility"
  | "opponent-plays-utility-item"
  | "opponent-plays-utility-maneuver"
  | "opponent-plays-utility-reaction"
  | "opponent-plays-attack"
  | "opponent-activates-ability"
  | "opponent-activates-counter"
  | "opponent-draws-card"
  | "opponent-gains-stamina"
  | "opponent-gains-life"
  | "self-armor-gains-wear"
  | "opponent-armor-gains-wear"
  | "self-received-damage"
  | "my-attack-dealt-damage"
  | "my-defend-full-blocked"
  | "any-priority-action";

export const ALL_REACTION_TRIGGERS: ReactionTrigger[] = [
  "opponent-plays-utility",
  "opponent-plays-utility-item",
  "opponent-plays-utility-maneuver",
  "opponent-plays-utility-reaction",
  "opponent-plays-attack",
  "opponent-activates-ability",
  "opponent-activates-counter",
  "opponent-draws-card",
  "opponent-gains-stamina",
  "opponent-gains-life",
  "self-armor-gains-wear",
  "opponent-armor-gains-wear",
  "self-received-damage",
  "my-attack-dealt-damage",
  "my-defend-full-blocked",
  "any-priority-action",
];

export interface UtilityFields {
  ability?: string;
  utilityType?: string;
  typeRequirement?: string[];
  /** Only meaningful when utilityType === "Reaction". List of trigger events
   *  this card can be played in response to during an Action Chain. */
  reactionTriggers?: ReactionTrigger[];
  /** Optional stamina cost to play this utility. Stored as string for parity
   *  with other field types. Missing/empty/non-numeric values are treated as
   *  0 (free). Deducted at play time, just like attack/defend cards. */
  staminaCost?: string;
}

export interface TokenFields {
  ability?: string;
  attackType?: string;
  blockValue?: string;
  defendType?: string;
  powerValue?: string;
  staminaCost?: string;
  attackWeight?: string;
  defendWeight?: string;
}

// ─── Shared structured-effect model ────────────────────────────────────────
// Used by both Counter rules and Mechanics. One row = one buff/debuff.

export type EffectDirection =
  | "buff-self"
  | "debuff-opponent"
  /** Affects the player's loadout (armor / weapons / sig moves / champion).
   *  Used by wear-counter and exhaust/ready effects. */
  | "loadout-self"
  | "loadout-opponent";

export type EffectTarget =
  | "my-next-attack"
  | "my-next-defend"
  | "my-next-action"
  | "this-attack"
  | "this-defend"
  | "this-action"
  | "popup-attack"
  | "popup-defend"
  | "popup-action"
  /** The player themselves — used by player-level effects (e.g. stamina). */
  | "self"
  | "opponent"
  /** Loadout targets — paired with `loadout-self` / `loadout-opponent`
   *  directions. Wear effects target armor; exhaust/ready target weapons.
   *  Sigs / champion are reserved for future loadout effects. */
  | "my-armor"
  | "opp-armor"
  | "my-weapons"
  | "opp-weapons"
  | "my-sigs"
  | "opp-sigs"
  | "my-champion"
  | "opp-champion";

export type EffectKind =
  | "power"
  | "block"
  | "add-keyword"
  | "remove-keyword"
  | "remove-power-buffs"
  | "remove-block-buffs"
  | "stamina-cost"
  /** Player-level instant stamina change. Sign comes from direction:
   *  buff-self = gain X, debuff-opponent = lose X. Target chooses who. */
  | "stamina"
  /** Player-level instant card draw. Sign comes from direction:
   *  buff-self = draw N for self, debuff-opponent = opponent discards N
   *  (NOT YET WIRED — only buff-self draw is implemented for now). Target
   *  must be `self` (or `opponent` for future discard variant). */
  | "draw"
  /** Loadout effects — mutate equipment/armor state directly. */
  | "add-wear"      // armor only — increases armorWearCounters
  | "remove-wear"   // armor only — decreases armorWearCounters (clamped at 0)
  | "exhaust"       // weapons only — sets EquipSlot.tapped = true on N slots
  | "ready"         // weapons only — sets EquipSlot.tapped = false on N slots
  /** Phase 3: search the player's deck for a card matching a filter and add
   *  it to hand. Drives generic tutor effects. Sub-fields on EffectRow:
   *  `searchFilter` (description), `searchPickCount` (default 1),
   *  `searchShuffleAfter` (default true). */
  | "search-deck"
  /** Phase 3: clear `stunned` and `wasStunnedThisRound` from the target.
   *  No amount needed. Used as a counter effect for Crimson Tempest to
   *  negate a stun played on the stack. Target should be `self` only. */
  | "clear-stun";

export interface EffectRow {
  direction: EffectDirection;
  target: EffectTarget;
  effect: EffectKind;
  amount?: number;       // for power / block / stamina-cost / stamina /
                         //     add-wear / remove-wear / weapon count for exhaust/ready
  keyword?: string;      // for add-keyword / remove-keyword
  keywordAmount?: number; // for add-keyword when the keyword takes a value
                         //     (e.g. Reflect 2, Disarm 3). Ignored for keywords
                         //     that don't take a value (Stun/Quick/Piercing/...)
  // Phase 3 — search-deck sub-fields
  /** Human-readable filter description shown in the tutor dialog title. */
  searchFilter?: string;
  /** Number of cards to pick (default 1). */
  searchPickCount?: number;
  /** Shuffle deck after picking (default true). */
  searchShuffleAfter?: boolean;
}

// ─── Mechanics (new structured form) ───────────────────────────────────────

export type MechanicAppliesWhen = "attack" | "defend" | "either";

export type MechanicCondition =
  | "none"
  | "attack-deals-damage"
  | "attack-fully-blocked"
  | "block-fully-blocks";

export interface MechanicEntry {
  appliesWhen: MechanicAppliesWhen;
  condition: MechanicCondition;
  effects: EffectRow[];

  // ── Legacy fields (retained for migration only — never written by the
  //    new editor). Loader auto-converts these into the structured shape.
  trigger?: "attack" | "defend";
  conditionType?: string;
  conditionValue?: number;
  effectType?: string;
  effectAmount?: number;
  effectScope?: "any" | "attack" | "defend";
}

// ─── Counter rules (new structured form) ───────────────────────────────────

/** Source-card kind filter for counter triggers. "attack-defend" = the
 *  opposing played card is an Attack/Defend card; "equipment" = an equipped
 *  weapon used as attack/defend (e.g. Hookfang Shiv swinging); "signature" =
 *  a signature move played as attack/defend. Empty list = no restriction. */
export type CardSourceKind = "attack-defend" | "equipment" | "signature";

export interface CounterTrigger {
  /** Restrict to the action the opponent played. "either" = no restriction. */
  actionPlayed: "attack" | "defend" | "either";
  /** Match if the opponent's card weight is in this list. Empty = any. */
  weights: string[];
  /** Match if the opponent's attack-type matches. Empty = any. */
  attackTypes: string[];
  /** Match if the opponent's defend-type matches. Empty = any. */
  defendTypes: string[];
  /** Match if the opponent's card has at least one of these keywords
   *  (printed OR buff-granted). Empty = any. */
  hasKeywords: string[];
  /** Match only if the opposing card's CURRENT power (after queued power-bonus
   *  buffs have fired and baked into the slot) is at least this value.
   *  Optional — undefined / 0 means no gate. */
  minBuffedPower?: number;
  /** Match only if the opposing card's CURRENT block (after queued block-bonus
   *  buffs have fired) is at least this value. Optional. */
  minBuffedBlock?: number;
  /** Restrict to opposing cards that come from a specific source kind. Empty
   *  list = no restriction. Used by No Time's "weapon attack" gate — set
   *  `cardKinds: ["equipment"]` to fire only when the opponent's attack came
   *  from an equipped weapon. Generic primitive — any future card with a
   *  source-kind requirement can use this. */
  cardKinds?: CardSourceKind[];
  /** Event-driven gate: counter only matches if the OPPONENT drew at least
   *  one card this round (cardsDrawnThisRound > 0). When set, the slot-match
   *  side of `ruleMatchesSlot` is skipped — the rule fires off the player's
   *  state alone, independent of any combat slot. Used by The Final Straw
   *  ("counter: opponent drew a card → you draw a card"). */
  opponentDrewThisRound?: boolean;
  /** Event-driven gate: counter only matches if YOU got stunned at least once
   *  this round (wasStunnedThisRound === true). Same shape as
   *  `opponentDrewThisRound` — slot-match side of `ruleMatchesSlot` is
   *  skipped when set. Used by Crimson Tempest's stun-response counter. */
  selfStunnedThisRound?: boolean;
  /** Event-driven gate: counter only matches if YOU took damage at least once
   *  this round (tookDamageThisRound === true). Used by reaction-style A/D
   *  counters (e.g. Calculate Risk). Slot-independent like its siblings. */
  selfTookDamageThisRound?: boolean;
}

export interface CounterRule {
  trigger: CounterTrigger;
  effects: EffectRow[];
}

// ─── Legacy counter shapes (kept ONLY for migration) ───────────────────────

export type CounterGrantKind =
  | "power-bonus"
  | "block-bonus"
  | "cost-reduction"
  | "gain-stun"
  | "gain-quick"
  | "gain-piercing"
  | "gain-reflect"
  | "gain-disarm"
  | "gain-chain";

export interface CounterGrant {
  kind: CounterGrantKind;
  amount?: number;
  count?: number;
  value?: number;
  power?: number;
}

export type CounterConditionType =
  | "always"
  | "opponent_card_weight"
  | "opponent_card_type";

export interface AttackDefendFields {
  weight?: string;
  mechanics?: MechanicEntry[];
  attackType?: string;
  blockValue?: string;
  defendType?: string;
  powerValue?: string;
  staminaCost?: string;
  attackAbility?: string;
  defendAbility?: string;
  isSpecialMove?: boolean;
  typeRequirement?: string[];

  // ── New structured counter rules (preferred). ─────────────────────────
  counterRules?: CounterRule[];

  // ── Display text for counter rules. Auto-generated from `counterRules`
  //    when the editor saves; designers can override. Kept on the card so
  //    the card preview can render flavor without re-deriving it. ────────
  counterAbility?: string;

  // ── Legacy counter fields (retained for migration only). ──────────────
  counterScope?: "attack" | "defend" | "any" | "";
  counterGrants?: CounterGrant[];
  counterRequirement?: string;
  counterConditionType?: CounterConditionType;
  counterConditionValue?: string;
}

// ─── Per-keyword config (numbers + Perry payload) ───────────────────────

/** Perry config: when a defend with Perry full-blocks an attack, the listed
 *  effects fire on the Perry user's `appliesTo` action.
 *  Reuses the shared EffectRow shape used by counters and mechanics. */
export interface PerryConfig {
  /** Which next action of the Perry user gets the effects. */
  appliesTo: "attack" | "defend" | "action";
  /** Effect payload — same shape as counter/mechanic effects. */
  effects: EffectRow[];
}

/** Per-card config for keywords whose behavior takes parameters.
 *  Legacy shared config — retained for back-compat. New per-slot config lives
 *  in `Card.keywordSlots` (per attack/defend slot, keyed by keyword name).
 *  When both are present, slot config wins. */
export interface KeywordConfig {
  /** Reflect amount X. Used when the card has the "Reflect" keyword. */
  reflect?: number;
  /** Disarm count X. Used when the card has the "Disarm" keyword. */
  disarm?: number;
  /** Perry payload. Used when the card has the "Perry" keyword. */
  perry?: PerryConfig;
}

/** Per-keyword-instance config. Each keyword chip in attack/defend slot can
 *  carry its own numbers/payload. Only fields relevant to the keyword name
 *  are used by the engine. */
export interface KeywordInstanceConfig {
  reflect?: number;
  disarm?: number;
  perry?: PerryConfig;
}

/** Per-slot keyword config. Keys are lowercase keyword names. */
export interface KeywordSlots {
  attack?: Record<string, KeywordInstanceConfig>;
  defend?: Record<string, KeywordInstanceConfig>;
}

export type KeywordSide = "attack" | "defend";

export interface Card {
  id: string;
  set: string;
  name: string;
  rarity: string;
  status: CardStatus;
  cardType: CardType;
  keywords: string[];
  createdAt: string;
  updatedAt?: string;
  imageName?: string;
  imageDataUrl?: string;
  mechanics?: string[]; // string tags for filtering
  rulesText?: string;
  designerNotes?: string;
  playtestNotes?: string;
  balanceConcerns?: string;
  setCardNumber?: string;

  // type-specific
  championFields?: ChampionFields;
  armorFields?: ArmorFields;
  equipmentFields?: EquipmentFields;
  signatureMoveFields?: SignatureMoveFields;
  stanceFields?: StanceFields;
  utilityFields?: UtilityFields;
  tokenFields?: TokenFields;
  attackDefendFields?: AttackDefendFields;

  attackKeywords?: string[];
  defendKeywords?: string[];

  /** Legacy shared per-keyword config. New cards use `keywordSlots`. */
  keywordConfig?: KeywordConfig;

  /** Per-slot keyword config. Each keyword chip in attack/defend has its own
   *  config keyed by lowercase keyword name. Engine prefers this over the
   *  legacy shared `keywordConfig`. */
  keywordSlots?: KeywordSlots;
}
